import { AXIS_KEYS } from '@/domain/rig'
import type { HumanoidModel } from '@/domain/hs1'

/**
 * Die Modellkennung — welcher Stand steckt in dieser Datei?
 *
 * DIE ZÄSUR DER WERKZEUGKETTE LIEGT HINTER STATION 2: ab dort gibt es nur noch
 * erzeugte Kopien. MJCF, URDF, Bewegungsdatensatz, LeRobot-Datensatz, ein
 * Trainingslauf, ein Regler, ein Bericht — sieben Dinge, die alle auf denselben
 * Modellstand zurückgehen sollen und keines, das es beweisen konnte.
 *
 * WAS BISHER DASTAND, war ein Satz Fliesstext: „erzeugt aus dem Punktsatz".
 * Das ist wahr und nutzlos. Es beantwortet nicht die eine Frage, die nach einem
 * halben Jahr gestellt wird: <b>welcher</b> Punktsatz? Zwei Dateien aus zwei
 * Ständen sehen gleich aus, bis jemand sie Zeile für Zeile vergleicht — und
 * genau das tut niemand, wenn eine Nacht Rechenzeit dranhängt.
 *
 * MIT DER KENNUNG steht in jeder erzeugten Datei dieselbe kurze Zeichenfolge,
 * und zwei Dateien aus verschiedenen Ständen unterscheiden sich auf den ersten
 * Blick. Ein Trainingslauf, der `hs2-a3f91c` im Datensatz stehen hat, gehört zu
 * genau einem Modell.
 *
 * WAS EINGEHT UND WAS NICHT — und das ist die eigentliche Entscheidung:
 *
 *   – EIN: alles, was die Maschine ausmacht. Gelenkpunkte, Massen mit
 *     Schwerpunkt und Trägheitsradius, Achsfreiheiten mit Grenzen und
 *     Momenten, Massstab, gewollte Unsymmetrie.
 *   – AUS: Beschriftungen. Ein umbenannter Punkt ist derselbe Roboter. Wer
 *     Namen mit aufnimmt, entwertet mit jeder Textkorrektur alle Datensätze,
 *     die es gibt — und das lehrt die Leute, die Kennung zu ignorieren.
 *   – AUS: der Zustand. Gangart, Zuladung und Trimmung gehören zum SZENARIO
 *     und nicht zur Maschine. Zwei Gangarten desselben Roboters müssen
 *     dieselbe Kennung tragen, sonst lässt sich nicht sagen, dass sie
 *     zusammengehören.
 *
 * KEINE KRYPTOGRAPHIE. Das hier schützt vor Verwechslung, nicht vor Täuschung.
 * Ein Fremder, der eine Datei fälschen will, ist nicht das Problem; die
 * Verwechslung zweier eigener Stände ist es. FNV-1a ist dafür genug, läuft
 * überall gleich und braucht keine Web-Crypto-Schnittstelle, die im Prüfstand
 * und in Node verschieden ist.
 */

/** Sechs Stellen — dieselbe Auflösung, mit der auch das MJCF schreibt. */
const fixed = (v: number): string => (Number.isFinite(v) ? v.toFixed(6) : 'nan')

/**
 * Der Roboter als kanonischer Text.
 *
 * SORTIERT UND IN FESTER FORM. Zwei gleiche Modelle müssen denselben Text
 * ergeben, auch wenn ihre Felder in anderer Reihenfolge im Speicher stehen —
 * `JSON.stringify` gibt das nicht her, weil es die Einfügereihenfolge behält.
 */
export function canonicalModel(model: HumanoidModel): string {
  const zeilen: string[] = []

  zeilen.push(`scale ${fixed(model.scale)}`)

  for (const key of Object.keys(model.points).sort()) {
    const p = model.points[key as keyof typeof model.points]
    if (!p) continue
    zeilen.push(`p ${key} ${fixed(p.x)} ${fixed(p.y)} ${fixed(p.z)} ${p.dof ?? '-'}`)
  }

  for (const key of Object.keys(model.masses).sort()) {
    const m = model.masses[key as keyof typeof model.masses]
    if (!m) continue
    zeilen.push(
      `m ${key} ${fixed(m.kg)} ${fixed(m.com)} ${fixed(m.gyration)} ` +
        `${m.paired ? 1 : 0} ${m.length === null ? '-' : fixed(m.length)}`,
    )
  }

  for (const key of Object.keys(model.dofs).sort()) {
    const dof = model.dofs[key as keyof typeof model.dofs]
    if (!dof) continue
    for (const axis of AXIS_KEYS) {
      const f = dof[axis]
      zeilen.push(
        f
          ? `d ${key}.${axis} ${fixed(f.lo)} ${fixed(f.hi)} ${fixed(f.moment)}`
          : `d ${key}.${axis} -`,
      )
    }
  }

  /* Eine gewollte Unsymmetrie ist eine Konstruktionsentscheidung und gehört
     deshalb in die Kennung. */
  for (const key of Object.keys(model.asym ?? {}).sort()) {
    const a = model.asym?.[key as keyof NonNullable<typeof model.asym>]
    if (!a) continue
    zeilen.push(`a ${key} ${fixed(a.x ?? 0)} ${fixed(a.y ?? 0)} ${fixed(a.z ?? 0)}`)
  }

  return zeilen.join('\n')
}

/**
 * FNV-1a, 64 bit.
 *
 * Über `BigInt`, damit die Zahl nicht bei 2^53 stillschweigend ungenau wird —
 * ein Prüfwert, der auf einer Maschine anders herauskommt als auf einer
 * anderen, ist schlimmer als keiner.
 */
function fnv1a(text: string): bigint {
  const MASK = (1n << 64n) - 1n
  const PRIME = 1099511628211n
  let hash = 14695981039346656037n
  for (let i = 0; i < text.length; i++) {
    hash = (hash ^ BigInt(text.charCodeAt(i) & 0xff)) & MASK
    hash = (hash * PRIME) & MASK
    /* Zeichen über 255 gehen als zweites Byte ein, damit ein Umlaut nicht mit
       einem anderen Zeichen zusammenfällt. */
    const hoch = text.charCodeAt(i) >> 8
    if (hoch) {
      hash = (hash ^ BigInt(hoch)) & MASK
      hash = (hash * PRIME) & MASK
    }
  }
  return hash
}

/**
 * Die Kennung eines Modellstandes: `hs2-a3f91c`.
 *
 * SECHS STELLEN, weil sie gelesen und verglichen werden — von Menschen, in
 * einem Dateikopf. Sechs Hexstellen sind sechzehn Millionen Möglichkeiten; die
 * Zahl der Modellstände dieses Projekts wird das nie ausreizen. Wer mehr
 * braucht, nimmt `modelDigest`.
 */
export function modelId(model: HumanoidModel, prefix = 'gen5'): string {
  return `${prefix}-${modelDigest(model).slice(0, 6)}`
}

/** Der volle Prüfwert, sechzehn Hexstellen. */
export function modelDigest(model: HumanoidModel): string {
  return fnv1a(canonicalModel(model)).toString(16).padStart(16, '0')
}
